import { Button, Tabs } from '@artosera/ui';
import { Eye, Globe } from 'lucide-react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { estApiError } from '../../api/errors.js';
import { useSite } from '../../api/site.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ApercuOverlay } from './ApercuOverlay.js';
import { ReglagesTab } from './tabs/ReglagesTab.js';
import { CharteTab } from './tabs/CharteTab.js';
import { PagesTab } from './tabs/PagesTab.js';
import { RedirectionsTab } from './tabs/RedirectionsTab.js';
import { PublicationTab } from './tabs/PublicationTab.js';
import styles from './SitePage.module.css';

/**
 * Site — réglages, charte, pages, redirections, publication (3.4, 4.1
 * `site`/`pages`, § 7.3, D2, D12, D16, S6-f). Cinq onglets, un bouton
 * « Aperçu » dans l'en-tête qui ouvre le site rendu avec les réglages
 * COURANTS, sans publier (D2 : « aperçu = même moteur sans publier »).
 */
export function SitePage(): JSX.Element {
  const { t } = useTranslation(['regie', 'site', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const reponse = useSite(galerieSlug);
  const [searchParams, setSearchParams] = useSearchParams();

  const peutPrevisualiser = galerieALaction(galerie, 'site:preview');
  const ongletActif = searchParams.get('onglet') ?? 'reglages';
  const apercuOuvert = searchParams.get('apercu') === '1';

  function ouvrirApercu(): void {
    const suivants = new URLSearchParams(searchParams);
    suivants.set('apercu', '1');
    setSearchParams(suivants);
  }
  function fermerApercu(): void {
    const suivants = new URLSearchParams(searchParams);
    suivants.delete('apercu');
    setSearchParams(suivants, { replace: true });
  }

  if (reponse.isLoading) {
    return <p className={styles.vide}>{t('commun:chargement')}</p>;
  }
  if (reponse.isError || !reponse.data) {
    return (
      <p className={styles.vide} role="alert">
        {estApiError(reponse.error) ? reponse.error.message : t('commun:erreurReseau')}
      </p>
    );
  }

  const { site, domaine } = reponse.data;

  return (
    <div className={styles.page}>
      <div className={styles.entete}>
        <div>
          <h1 className={styles.titre}>{t('site:titre')}</h1>
          <p className={styles.adresse}>
            <Globe size={14} strokeWidth={1.5} aria-hidden />
            <span className={styles.adresseTexte}>{domaine}</span>
          </p>
        </div>
        {peutPrevisualiser ? (
          <Button variant="secondary" size="sm" onClick={ouvrirApercu}>
            <Eye size={16} strokeWidth={1.5} aria-hidden />
            {t('commun:actions.apercu')}
          </Button>
        ) : null}
      </div>

      <div className={styles.onglets}>
        <Tabs
          value={ongletActif}
          onValueChange={(valeur) => {
            const suivants = new URLSearchParams(searchParams);
            suivants.set('onglet', valeur);
            setSearchParams(suivants, { replace: true });
          }}
          tabs={[
            {
              value: 'reglages',
              label: t('regie:site.onglets.reglages'),
              content: (
                <ReglagesTab
                  galerieSlug={galerieSlug}
                  site={site}
                  domaine={domaine}
                  galerie={galerie}
                />
              ),
            },
            {
              value: 'charte',
              label: t('regie:site.onglets.charte'),
              content: <CharteTab galerieSlug={galerieSlug} site={site} galerie={galerie} />,
            },
            {
              value: 'pages',
              label: t('regie:site.onglets.pages'),
              content: <PagesTab galerieSlug={galerieSlug} galerie={galerie} />,
            },
            {
              value: 'redirections',
              label: t('regie:site.onglets.redirections'),
              content: (
                <RedirectionsTab
                  galerieSlug={galerieSlug}
                  redirections={site.redirections}
                  galerie={galerie}
                />
              ),
            },
            {
              value: 'publication',
              label: t('regie:site.onglets.publication'),
              content: (
                <PublicationTab
                  galerieSlug={galerieSlug}
                  site={site}
                  domaine={domaine}
                  galerie={galerie}
                />
              ),
            },
          ]}
        />
      </div>

      <ApercuOverlay
        galerieSlug={galerieSlug}
        open={apercuOuvert}
        onClose={fermerApercu}
        langueParDefaut={site.langueParDefaut}
        languesActives={site.langues}
      />
    </div>
  );
}
